import type { DependencyList, JSX, ReactNode } from "react";
import {
    createContext,
    useContext, useEffect, useRef,
    useSyncExternalStore,
} from "react";
import type { Controller } from "@rosette/ts/src/business/game/controller/Controller.js";
import type { Directive } from "@rosette/ts/src/business/game/controller/Directive.js";
import { areDependenciesEqual } from "@rosette/ts/src/util/areDependenciesEqual.js";

interface ProviderProps {
    children: ReactNode;
}

export type ControllerProviderType = (props: ProviderProps) => JSX.Element;

interface ControllerCache<D extends Directive, C extends Controller<D>> {
    controller: Exclude<C, void>;
    dependencies: DependencyList;
}


/**
 * Creates a controller and uses useEffect to make its subscriptions
 * to child controllers and clean them up when the component unmounts.
 */
export function useCreateController<
    D extends Directive, C extends Controller<D>,
>(
    newControllerFn: () => Exclude<C, void>,
    dependencies: DependencyList,
): C {
    const cacheRef = useRef<ControllerCache<D, C> | null>(null);

    let controller: Exclude<C, void>;
    const current = cacheRef.current;
    if (current === null || !areDependenciesEqual(dependencies, current.dependencies)) {
        controller = newControllerFn();
        cacheRef.current = {
            controller: controller,
            dependencies: dependencies,
        };
    } else {
        controller = current.controller;
    }

    useEffect(() => controller.setup(), [controller]);
    return controller;
}


interface ControllerContextResult<D extends Directive, C extends Controller<D>> {
    ControllerProvider: (props: ProviderProps) => JSX.Element;
    useController: () => C;
    useOptionalController: () => C | null;
    useDirective: () => D;
    useDirectiveSelector: <V>(selector: (directive: D) => V) => V;
}


/**
 * Create context for a controller.
 */
export function createControllerContext<D extends Directive, C extends Controller<D>>(
    useCreateControllerFn: () => C,
): ControllerContextResult<D, C> {
    const ControllerContext = createContext<C | null>(null);

    function ControllerProvider({ children }: ProviderProps) {
        const controller = useCreateControllerFn();
        return (
            <ControllerContext.Provider value={controller}>
                {children}
            </ControllerContext.Provider>
        );
    }

    function useOptionalController(): C | null {
        return useContext(ControllerContext);
    }

    function useController(): C {
        const controller = useOptionalController();
        if (!controller)
            throw new Error("Controller not found");
        return controller;
    }

    function useDirectiveSelector<V>(selector: (directive: D) => V): V {
        const controller = useController();
        return useSyncExternalStore(
            callback => controller.subscribeToActiveDirective(callback),
            () => selector(controller.getActiveDirective()),
            () => selector(controller.getActiveDirective()),
        );
    }

    function useDirective(): D {
        return useDirectiveSelector(d => d);
    }

    return {
        ControllerProvider,
        useController,
        useOptionalController,
        useDirective,
        useDirectiveSelector,
    };
}
